import { faChevronCircleLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, Suspense } from 'react';
import {
  Redirect,
  Route,
  Switch,
  useLocation,
  useParams,
} from 'react-router-dom';
import {
  ContractManagementRequest,
  useContractManagement,
} from 'src/apis/contract-management/use-contract-management';
import { Shipper } from 'src/apis/monolith/types';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { Stack } from 'src/components/layout/stack';
import { Link } from 'src/components/navigation/link';
import { PageSpinner } from 'src/components/spinner-container';
import { Heading } from 'src/components/text/heading';
import { isNetworkError, Successful } from 'src/hooks/use-axios';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { FinancialSecurityDetails } from 'src/pages/operators/assignments/details/financial-securities/details';
import { ShipperAdmins } from 'src/pages/shippers/details/admins';
import { useShipperById } from 'src/pages/shippers/details/admins/use-shipper-by-id';
import { ShipperContracts } from 'src/pages/shippers/details/contracts';
import { ShippersContractDetails } from 'src/pages/shippers/details/contracts/contract-details';
import { EditCreditLimit } from 'src/pages/shippers/details/contracts/edit-credit-limit';
import { ShipperDocuments } from 'src/pages/shippers/details/documents';
import { ShipperFinancialSecurities } from 'src/pages/shippers/details/financial-securities';
import { FinancialSecurityAmendmentApproval } from 'src/pages/shippers/details/financial-securities/amendment-approval';
import { ShipperInformation } from 'src/pages/shippers/details/information';
import {
  ContractManagementShipperRequest,
  useContractManagementShipper,
} from 'src/pages/shippers/details/information/use-contract-management-shipper';
import { ShipperInformationSidebar } from 'src/pages/shippers/details/shipper-information-sidebar';
import {
  ShipperPlatformIdsRequest,
  useShipperPlatformIds,
} from 'src/pages/shippers/use-shipper-platform-ids';

export const ShipperDetails: FC = () => {
  return (
    <Switch>
      {/* old workaround for TAG-4209*/}
      <Route path="/mail/shippers/:shipperUuid/financial-securities/:financialSecurityType(cash-deposit|bank-guarantee|rating|parent-company-guarantee)/:financialSecurityId">
        <RedirectMailFinancialSecurity />
      </Route>

      <Route path="/dashboard/shippers/:shipperUuid/financial-securities/cash-deposit/:financialSecurityId/amendment/approve">
        <FinancialSecurityAmendmentApproval type="CASH_DEPOSIT" />
      </Route>
      <Route path="/dashboard/shippers/:shipperUuid/financial-securities/bank-guarantee/:financialSecurityId/amendment/approve">
        <FinancialSecurityAmendmentApproval type="BANK_GUARANTEE" />
      </Route>
      <Route path="/dashboard/shippers/:shipperUuid/financial-securities/rating/:financialSecurityId/amendment/approve">
        <FinancialSecurityAmendmentApproval type="RATING" />
      </Route>
      <Route path="/dashboard/shippers/:shipperUuid/financial-securities/parent-company-guarantee/:financialSecurityId/amendment/approve">
        <FinancialSecurityAmendmentApproval type="PARENT_COMPANY_GUARANTEE" />
      </Route>

      <Route path="/dashboard/shippers/:shipperUuid/financial-securities/cash-deposit/:financialSecurityId">
        <FinancialSecurityDetails type="CASH_DEPOSIT" />
      </Route>
      <Route path="/dashboard/shippers/:shipperUuid/financial-securities/rating/:financialSecurityId">
        <FinancialSecurityDetails type="RATING" />
      </Route>
      <Route path="/dashboard/shippers/:shipperUuid/financial-securities/bank-guarantee/:financialSecurityId">
        <FinancialSecurityDetails type="BANK_GUARANTEE" />
      </Route>
      <Route path="/dashboard/shippers/:shipperUuid/financial-securities/parent-company-guarantee/:financialSecurityId">
        <FinancialSecurityDetails type="PARENT_COMPANY_GUARANTEE" />
      </Route>

      <Route path="/dashboard/shippers/:shipperUuid">
        <LoadShipperId />
      </Route>

      <Route path="/shippers/:shipperRefId">
        <RedirectShipperPages />
      </Route>

      <ThrowNotFound />
    </Switch>
  );
};

const LoadShipperId = () => {
  const { shipperUuid } = useParams<{ shipperUuid: string }>();
  const shipperPlatformIds = useShipperPlatformIds(shipperUuid);

  if (!shipperPlatformIds.response) return <PageSpinner />;

  return <RoutesWithShipper shipperPlatformIds={shipperPlatformIds} />;
};

const RoutesWithShipper: FC<{
  shipperPlatformIds: Successful<ShipperPlatformIdsRequest>;
}> = ({ shipperPlatformIds }) => {
  const shipper = useShipperById(
    shipperPlatformIds.response.data.referenceId.toString()
  );
  const contractManagement = useContractManagement();

  if (
    !shipper.response ||
    (!contractManagement.response && !isNetworkError(contractManagement.error))
  )
    return <PageSpinner />;

  return (
    <Switch>
      <Route path="/dashboard/shippers/:shipperUuid/admins">
        <ShipperAdmins shipper={shipper} />
      </Route>

      <Route>
        {contractManagement.response?.data._links.shipper ? (
          <LoadContractManagementShipper
            shipper={shipper.response.data}
            contractManagement={contractManagement}
            shipperPlatformIds={shipperPlatformIds}
          />
        ) : (
          <PagesWithSidebar
            shipper={shipper.response.data}
            contractManagement={contractManagement}
            shipperPlatformIds={shipperPlatformIds}
          />
        )}
      </Route>
    </Switch>
  );
};

type LoadContractManagementShipperProps = {
  shipper: Shipper;
  contractManagement: ContractManagementRequest;
  shipperPlatformIds: Successful<ShipperPlatformIdsRequest>;
};

const LoadContractManagementShipper: FC<LoadContractManagementShipperProps> = ({
  shipper,
  contractManagement,
  shipperPlatformIds,
}) => {
  const contractManagementShipper = useContractManagementShipper({
    shipperUuid: shipper.organizationId,
    shipperUrl: contractManagement.response!.data._links.shipper!.href,
  });

  if (
    !contractManagementShipper.response?.data ||
    contractManagementShipper.pending
  )
    return <PageSpinner />;

  return (
    <PagesWithSidebar
      shipper={shipper}
      contractManagement={contractManagement}
      contractManagementShipper={contractManagementShipper}
      shipperPlatformIds={shipperPlatformIds}
    />
  );
};

type PagesWithSidebarProps = {
  shipper: Shipper;
  contractManagement: ContractManagementRequest;
  contractManagementShipper?: ContractManagementShipperRequest;
  shipperPlatformIds: Successful<ShipperPlatformIdsRequest>;
};

const PagesWithSidebar: FC<PagesWithSidebarProps> = ({
  shipper,
  contractManagement,
  contractManagementShipper,
  shipperPlatformIds,
}) => {
  const { minTablet } = useBreakpoints();

  return (
    <Stack gap={1}>
      <Link to="/shippers/list">
        <FontAwesomeIcon icon={faChevronCircleLeft} aria-hidden /> Back to
        Shippers
      </Link>

      <Heading mode="title">{shipper.name}</Heading>

      <Stack
        gap={[1, 3]}
        templateColumns={minTablet ? '1fr 5fr' : '1fr'}
        alignItems="start"
      >
        <ShipperInformationSidebar
          contractManagement={contractManagement.response?.data}
          contractManagementShipper={contractManagementShipper?.response?.data}
          shipperPlatformIds={shipperPlatformIds}
        />

        <div>
          <Suspense fallback={<PageSpinner />}>
            <Switch>
              <Route path="/dashboard/shippers/:shipperUuid/contracts/:contractType(fmct|dect|lect|fmct-offer|dect-offer)/:contractId/details">
                <ShippersContractDetails />
              </Route>

              <Route path="/dashboard/shippers/:shipperUuid/contracts/lect/:contractId/edit-credit-limit">
                <EditCreditLimit />
              </Route>

              <Route path="/dashboard/shippers/:shipperUuid/contracts">
                <ShipperContracts
                  shipper={shipper}
                  contractManagement={contractManagement}
                />
              </Route>

              <Route path="/dashboard/shippers/:shipperUuid/documents">
                <ShipperDocuments
                  shipper={shipper}
                  shipperPlatformIds={shipperPlatformIds}
                />
              </Route>

              <Route path="/dashboard/shippers/:shipperUuid/information">
                <ShipperInformation
                  shipper={shipper}
                  contractManagement={contractManagement}
                  shipperPlatformIds={shipperPlatformIds}
                />
              </Route>

              <Route path="/dashboard/shippers/:shipperUuid/financial-securities">
                <ShipperFinancialSecurities
                  shipper={shipper}
                  contractManagementShipper={contractManagementShipper}
                />
              </Route>

              <Redirect
                from="/dashboard/shippers/:shipperUuid"
                exact
                to="/dashboard/shippers/:shipperUuid/information"
              />

              <ThrowNotFound />
            </Switch>
          </Suspense>
        </div>
      </Stack>
    </Stack>
  );
};

const RedirectShipperPages: FC = () => {
  const { shipperRefId } = useParams<{ shipperRefId: string }>();
  const { pathname, search, state } = useLocation();
  const shipper = useShipperById(shipperRefId);

  if (!shipper.response) return <PageSpinner />;

  return (
    <Redirect
      to={{
        pathname: pathname.replace(
          `/shippers/${shipperRefId}`,
          `/dashboard/shippers/${shipper.response.data.organizationId}`
        ),
        search,
        state,
      }}
    />
  );
};

const RedirectMailFinancialSecurity: FC = () => {
  const { shipperUuid, financialSecurityType, financialSecurityId } =
    useParams<{
      shipperUuid: string;
      financialSecurityType:
        | 'cash-deposit'
        | 'bank-guarantee'
        | 'rating'
        | 'parent-company-guarantee';
      financialSecurityId: string;
    }>();

  return (
    <Redirect
      to={`/dashboard/shippers/${shipperUuid}/financial-securities/${financialSecurityType}/${financialSecurityId}`}
    />
  );
};
